import { z } from 'zod';
import { AuditMeta, DocId, IsoTimestamp, TenantId } from '../common.js';
import { IsoDate } from './security-incident.js';
/**
 * Kostendeclaratie — vervangt het Microsoft Forms-declaratieformulier en het
 * Word-formulier "Kostendeclaratie" (plan: docs/plans/declaraties.md).
 *
 * Firestore path: declaraties/{id}
 * Root (niet tenant-subcollectie) omdat teamleiders en kantoor in één wachtrij
 * beoordelen, zelfde motivering als security_incidents. Elk document stempelt
 * zijn eigen tenantId + submittedByUserId server-side.
 *
 * Kernbesluiten (Jos, 20 aug 2026):
 *  - De toegewezen teamleider geeft eerst akkoord; kantoor/admin is vangnet.
 *  - Versturen naar de administratie doet de app zelf (Resend, besluit Jos
 *    10 sep 2026; verving de mailto-link): PDF + bonnen als bijlage, status
 *    `verstuurd` pas na een geslaagde verzending.
 *  - Rekeningnummer kan in het profiel staan en wordt voorgevuld.
 */
/**
 * IBAN met mod-97-controle. De transform normaliseert (spaties weg,
 * hoofdletters) zodat "nl91 abna 0417 1643 00" gewoon werkt; de check vangt
 * overtypfouten — dit is het nummer waar de administratie geld naartoe boekt.
 */
export const Iban = z
    .string()
    .transform((s) => s.replace(/\s+/g, '').toUpperCase())
    .pipe(z
    .string()
    .regex(/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/, 'Voer een geldig IBAN in (bijv. NL91ABNA0417164300)')
    .refine(ibanMod97, 'Dit IBAN bestaat niet — controleer het nummer op typfouten'));
/** ISO 13616 mod-97: land+controlegetal naar achteren, letters → cijfers, rest 1. */
export function ibanMod97(iban) {
    const herschikt = iban.slice(4) + iban.slice(0, 4);
    let rest = 0;
    for (const ch of herschikt) {
        const v = ch >= 'A' ? ch.charCodeAt(0) - 55 : Number(ch);
        rest = (rest * (v > 9 ? 100 : 10) + v) % 97;
    }
    return rest === 1;
}
/** Gemaskeerd voor lijstweergaven: alleen land + laatste 3 cijfers. */
export function maskIban(iban) {
    if (iban.length < 8)
        return '****';
    return `${iban.slice(0, 4)}****${iban.slice(-3)}`;
}
/** Eén kostenregel uit het Word-formulier (datum, omschrijving, bedrag). */
export const DeclaratieRegel = z.object({
    datum: IsoDate,
    omschrijving: z.string().min(1).max(500),
    /**
     * In centen, geheel getal — nooit floats voor geld. Plafond €10.000 per
     * regel: een tikfout met een extra nul hoort hier te stranden, niet bij de
     * administratie.
     */
    bedragCent: z.number().int().positive().max(1_000_000, 'Maximaal €10.000 per regel'),
});
/**
 * Bijlagetypen conform het Forms-formulier (besluit Jos 20 aug: alle types).
 * mimeType → extensie; de poort zit op deze map, niet op de bestandsnaam.
 */
export const BON_EXT = {
    'application/pdf': 'pdf',
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
};
/** Zelfde grenzen als Forms: max 9 bestanden, 10 MB per stuk. */
export const BON_MAX_AANTAL = 9;
export const BON_MAX_BYTES = 10 * 1024 * 1024;
/**
 * Totaal per declaratie: de bonnen gaan als bijlage mee in de mail naar de
 * administratie en Resend staat 40 MB per mail toe inclusief base64 (besluit
 * Jos 10 sep 2026: grenzen gelijktrekken, niet comprimeren).
 */
export const BON_MAX_TOTAAL_BYTES = 25 * 1024 * 1024;
/**
 * Een geüpload bonnetje of factuur. `object` is het GCS-objectpad (nooit een
 * URL) in de privé-bonnenbucket; het pad is server-gemunt en begint met het
 * userId van de uploader, zodat niemand andermans object kan claimen.
 */
export const DeclaratieBon = z.object({
    object: z.string().min(1).max(300),
    filename: z.string().min(1).max(200),
    mimeType: z.string().refine((v) => v in BON_EXT, {
        message: 'Alleen PDF, afbeelding, Word of Excel',
    }),
    sizeBytes: z.number().int().positive().max(BON_MAX_BYTES),
});
/**
 * Levensloop: ingediend → akkoord → verstuurd, of ingediend → afgewezen.
 * `akkoord` en `verstuurd` blijven twee bewuste handelingen van de
 * beoordelaar; de tweede verstuurt de mail server-side.
 */
/**
 * Soorten die niet naar de teamleider gaan maar rechtstreeks naar kantoor
 * (verzoek Marjolein 30 sep 2026): de VOG, en reiskosten zolang iemand de
 * NS-businesscard nog niet heeft. Geen soort = gewone declaratie, teamleider
 * eerst. De medewerker kiest de soort, de route volgt daaruit. De
 * POH GGZ-opleiding kwam erbij op verzoek van Marjolein (5 okt 2026).
 */
export const DeclaratieSoort = z.enum(['vog', 'reiskosten_zonder_ns', 'poh_ggz_opleiding']);
export const DECLARATIE_SOORT_LABEL = {
    vog: 'VOG',
    reiskosten_zonder_ns: 'Reiskosten (nog geen NS-businesscard)',
    poh_ggz_opleiding: 'POH GGZ-opleiding',
};
export const DeclaratieStatus = z.enum(['ingediend', 'akkoord', 'afgewezen', 'verstuurd']);
export const DeclaratieSchema = AuditMeta.extend({
    id: DocId,
    tenantId: TenantId,
    submittedByUserId: DocId,
    /** Kort, uitspreekbaar kenmerk (DECL-XXXXXX), zelfde patroon als MELD-. */
    declaratienummer: z.string().max(20),
    /** Weergavenaam van de indiener, snapshot bij indienen (voor lijst + mail). */
    naam: z.string().min(1).max(200),
    /** Bijv. "juli 2026" — vrije tekst, zoals op het Word-formulier. */
    periode: z.string().min(1).max(100),
    rekeningnummer: Iban,
    /** Exact vereist een woonplaats bij uitbetalen. Optioneel: declaraties van vóór 26 okt 2026 hebben hem niet. */
    woonplaats: z.string().max(100).optional(),
    regels: z.array(DeclaratieRegel).min(1).max(20),
    /** Server-side berekend uit de regels, nooit door de client aangeleverd. */
    totaalCent: z.number().int().positive(),
    bonnen: z.array(DeclaratieBon).max(BON_MAX_AANTAL).default([]),
    status: DeclaratieStatus.default('ingediend'),
    soort: DeclaratieSoort.optional(),
    /**
     * De teamleider van de indiener, snapshot bij indienen uit het User-record.
     * Afwezig = geen teamleider toegewezen; kantoor/admin beoordeelt dan direct.
     */
    teamleiderUserId: DocId.optional(),
    /**
     * Kantoorpersoneel (isHeadOffice) declareert zonder akkoordstap: de
     * declaratie gaat bij indienen meteen naar de administratie (verzoek Linda
     * 5 okt 2026). Alleen server-side gezet, na de isHeadOffice-check.
     */
    directNaarAdministratie: z.boolean().optional(),
    afwijzingsreden: z.string().max(2000).optional(),
    /**
     * Deels akkoord (Jos 12 okt 2026, bijv. lidmaatschappen): de beoordelaar
     * vergoedt een percentage van het totaal. Afwezig = volledig vergoed.
     * `vergoedCent` berekent de server, afgerond op hele centen.
     */
    vergoedingPercentage: z.number().int().min(1).max(99).optional(),
    vergoedCent: z.number().int().positive().optional(),
    beoordeeldDoorUserId: DocId.optional(),
    beoordeeldOp: IsoTimestamp.optional(),
    verstuurdDoorUserId: DocId.optional(),
    verstuurdOp: IsoTimestamp.optional(),
});
/** Wat de browser post. Naam, tenant, teamleider en totaal stempelt de route. */
export const SubmitDeclaratieInput = z.object({
    periode: z.string().min(1).max(100),
    rekeningnummer: Iban,
    woonplaats: z.string().trim().min(1, 'Vul je woonplaats in').max(100),
    regels: z.array(DeclaratieRegel).min(1).max(20),
    soort: DeclaratieSoort.optional(),
    /**
     * Alleen voor kantoorpersoneel: geen teamleider-akkoord, direct naar de
     * administratie. De route weigert dit voor iedereen zonder isHeadOffice.
     */
    directNaarAdministratie: z.literal(true).optional(),
    bonnen: z
        .array(DeclaratieBon)
        .max(BON_MAX_AANTAL)
        .default([])
        .refine((b) => b.reduce((sum, bon) => sum + bon.sizeBytes, 0) <= BON_MAX_TOTAAL_BYTES, {
        message: `Alle bonnen samen mogen maximaal ${BON_MAX_TOTAAL_BYTES / (1024 * 1024)} MB zijn`,
    }),
});
export const ReviewDeclaratieInput = z
    .object({
    action: z.enum(['akkoord', 'afwijzen']),
    reden: z.string().max(2000).optional(),
    /** Alleen bij akkoord: deels vergoeden. */
    vergoedingPercentage: z.number().int().min(1).max(99).optional(),
})
    .refine((v) => v.action !== 'afwijzen' || !!v.reden?.trim(), {
    message: 'Geef een reden voor de afwijzing.',
    path: ['reden'],
})
    .refine((v) => v.action === 'akkoord' || v.vergoedingPercentage === undefined, {
    message: 'Een percentage hoort alleen bij akkoord.',
    path: ['vergoedingPercentage'],
});
/** Teamleider-toewijzing door de beheerder (Linda). `null` = toewijzing weg. */
export const SetTeamleiderInput = z.object({
    teamleiderUserId: DocId.nullable(),
});
/** Markeer een gebruiker als (niet-)teamleider — beheerderskant. */
export const MarkeerTeamleiderInput = z.object({
    isTeamleider: z.boolean(),
});
//# sourceMappingURL=declaratie.js.map